import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, ShieldCheck, Trash2, UserPlus, Users } from 'lucide-react';
import { toast } from 'sonner';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../../../components/ui/alert-dialog';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { Label } from '../../../components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../../components/ui/select';
import { PersonSelect } from '../../../components/ui/person-select';
import { BranchContact } from '../../../types/lists.types';
import { StagedMember, Yard, YardMember, YardRole } from '../../../types/stock.types';
import { yardsApi } from '../../../utils/stock-api';
import { cn } from '../../../components/ui/utils';

interface Props {
    /** The saved yard, or null while creating one that does not exist yet. */
    yard?: Yard | null;
    /** Every user — membership is not restricted by job role. */
    users: BranchContact[];
    /** Create mode: the staged list, held by the form until it submits. */
    staged?: StagedMember[];
    onStagedChange?: (next: StagedMember[]) => void;
    /** Create mode: the "needs a Yard Manager" error, raised by the form on submit. */
    error?: string;
}

const ROLE_LABEL: Record<YardRole, string> = {
    member: 'Member',
    yard_manager: 'Yard Manager',
};

/** One row of the list, however it is stored. */
interface Row {
    key: string;
    userId: number;
    name: string;
    email?: string;
    yardRole: YardRole;
    /** Present only for rows that exist server-side. */
    memberId?: number;
}

/**
 * Yard membership (STOCK-MENU.md §5.4) — the second section of both Create and Manage.
 *
 * Membership is what lets someone SEE a yard at all, and since the yardManager column
 * was dropped it is also what makes someone a Yard Manager. It is separate from the §11
 * permission matrix, and the two combine:
 *
 *     effective rights = (member of this yard OR a global role) AND (role holds the permission)
 *
 * Global roles — Admin, Account Owner, Stock Controller — bypass this list entirely and
 * are not shown in it, because they were never added to it.
 *
 * TWO MODES, one list:
 *
 *   Saved yard  — every action writes immediately. These are rows in their own table,
 *                 not fields of the yard, so batching them behind Save Changes would
 *                 make "add someone, then discard the form" ambiguous.
 *   New yard    — there is no id to write against, so rows are staged in the parent form
 *                 and sent with the create call, which commits them in one transaction.
 *
 * The rendering is shared; only the four handlers branch.
 */
export const YardMembersSection: React.FC<Props> = ({
    yard,
    users,
    staged,
    onStagedChange,
    error,
}) => {
    const live = Boolean(yard);

    const [members, setMembers] = useState<YardMember[]>([]);
    const [loading, setLoading] = useState(live);
    const [busy, setBusy] = useState<string | null>(null);
    const [addUserId, setAddUserId] = useState<string>('');
    const [addRole, setAddRole] = useState<YardRole>('member');
    /** The row awaiting removal confirmation, via the shared destructive dialog. */
    const [pendingRemove, setPendingRemove] = useState<Row | null>(null);

    /**
     * How much work is waiting on a manager here. Approval is a queue rather than a
     * per-order assignment, so changing who manages the yard hands this over intact —
     * but the admin doing it should see the size of what they are handing over.
     */
    const [queue, setQueue] = useState<{ pending: number; activeManagers: number } | null>(null);

    const load = useCallback(async () => {
        if (!yard) return;
        try {
            setLoading(true);
            const [rows, q] = await Promise.all([
                yardsApi.members(yard.id),
                yardsApi.approvalQueue(yard.id).catch(() => null),
            ]);
            setMembers(rows);
            setQueue(q);
        } catch (err: any) {
            toast.error(err?.message || 'Failed to load members');
        } finally {
            setLoading(false);
        }
    }, [yard]);

    useEffect(() => { if (live) load(); }, [live, load]);

    const userOptions = useMemo(() => (Array.isArray(users) ? users : []), [users]);

    // Both shapes collapse to the same row so the list below renders once.
    const rows: Row[] = useMemo(() => {
        if (live) {
            return members.map((m) => ({
                key: `m${m.id}`,
                userId: m.user?.id ?? 0,
                name: m.user?.name ?? 'Unknown',
                email: m.user?.email,
                yardRole: m.yardRole,
                memberId: m.id,
            }));
        }
        return (staged ?? []).map((s) => {
            const u = userOptions.find((o) => o.id === s.userId);
            return {
                key: `s${s.userId}`,
                userId: s.userId,
                name: u?.name ?? 'Unknown',
                email: u?.email,
                yardRole: s.yardRole,
            };
        });
    }, [live, members, staged, userOptions]);

    const memberUserIds = useMemo(
        () => new Set(rows.map((r) => r.userId).filter(Boolean)),
        [rows]
    );

    // Someone already in the list must not be offerable again — the unique constraint
    // would reject it, but a selector that can only fail is a bad selector.
    const addable = useMemo(
        () => userOptions.filter((u) => !memberUserIds.has(u.id)),
        [userOptions, memberUserIds]
    );

    const managerCount = rows.filter((r) => r.yardRole === 'yard_manager').length;
    const frozen = yard?.status === 'closed';

    const run = async (key: string, fn: () => Promise<any>, message?: string) => {
        try {
            setBusy(key);
            await fn();
            if (message) toast.success(message);
            await load();
        } catch (err: any) {
            toast.error(err?.data?.message || err?.message || 'Something went wrong');
        } finally {
            setBusy(null);
        }
    };

    const handleAdd = async () => {
        if (!addUserId) return;
        const userId = Number(addUserId);

        if (!live) {
            onStagedChange?.([...(staged ?? []), { userId, yardRole: addRole }]);
            setAddUserId('');
            setAddRole('member');
            return;
        }
        await run('add', async () => {
            await yardsApi.addMember(yard!.id, userId, addRole);
            setAddUserId('');
            setAddRole('member');
        }, 'Member added');
    };

    const handleRoleChange = (row: Row, next: YardRole) => {
        if (!live) {
            onStagedChange?.((staged ?? []).map(
                (s) => (s.userId === row.userId ? { ...s, yardRole: next } : s)));
            return;
        }
        run(`role:${row.memberId}`,
            () => yardsApi.setMemberRole(yard!.id, row.memberId!, next),
            'Role updated');
    };

    const handleRemove = (row: Row) => {
        if (!live) {
            // Nothing is saved yet, so no confirmation — the cost of a mistake is one click.
            onStagedChange?.((staged ?? []).filter((s) => s.userId !== row.userId));
            return;
        }
        setPendingRemove(row);
    };

    const confirmRemove = async () => {
        const row = pendingRemove;
        if (!row) return;
        await run(`remove:${row.memberId}`,
            () => yardsApi.removeMember(yard!.id, row.memberId!),
            'Member removed');
        setPendingRemove(null);
    };

    return (
        <Card className={cn(error && 'border-destructive')}>
            <CardContent className="p-4 md:p-6 space-y-5">
                <div>
                    <h2 className="text-base font-semibold flex items-center gap-2">
                        <Users size={18} className="text-muted-foreground" />Members
                    </h2>
                    <p className="text-sm text-muted-foreground mt-1">
                        Members see this yard and its Deliveries, Returns and Import Orders. Admins,
                        Account Owners and Stock Controllers see every yard without being added here.
                        {live && !frozen && ' Changes here save immediately.'}
                        {!live && ' They are created together with the yard.'}
                    </p>
                </div>

                {/*
                  * Approvals follow the ROLE, not the person: nothing is assigned to an
                  * individual, so a new manager picks up this queue the moment they are
                  * promoted, and a demoted one simply stops seeing it.
                  */}
                {live && !frozen && queue !== null && queue.pending > 0 && (
                    <p className="text-sm text-blue-800 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                        <strong>{queue.pending}</strong>{' '}
                        {queue.pending === 1 ? 'order is' : 'orders are'} waiting on a Yard
                        Manager here. Approvals are not assigned to a person — whoever holds
                        the role can action them, so changing managers hands this queue over
                        rather than stranding it.
                    </p>
                )}

                {/* A yard with no manager has nobody who can approve within it (§5.2). */}
                {!frozen && managerCount === 0 && (
                    <p className={cn(
                        'text-sm rounded-lg px-3 py-2 border',
                        error
                            ? 'text-destructive bg-destructive/5 border-destructive/30'
                            : 'text-amber-700 bg-amber-50 border-amber-200'
                    )}>
                        {error || 'This yard has no Yard Manager. Add at least one.'}
                    </p>
                )}

                {frozen && (
                    <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                        This yard is closed. Membership is frozen — existing members keep read-only
                        access to the archive, which is why they cannot be removed.
                    </p>
                )}

                {/* Add */}
                {!frozen && (
                    <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr_auto] gap-2 items-end">
                        <div className="space-y-2">
                            <Label htmlFor="member-user">Add a person</Label>
                            <PersonSelect
                                id="member-user"
                                people={addable}
                                value={addUserId}
                                onChange={setAddUserId}
                                disabled={addable.length === 0}
                                placeholder={
                                    addable.length ? 'Select a person' : 'Everyone is already a member'
                                }
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="member-role">Yard role</Label>
                            <Select value={addRole} onValueChange={(v) => setAddRole(v as YardRole)}>
                                <SelectTrigger id="member-role"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="member">Member</SelectItem>
                                    <SelectItem value="yard_manager">Yard Manager</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <Button
                            type="button"
                            onClick={handleAdd}
                            disabled={!addUserId || busy !== null}
                            className="bg-slate-900 hover:bg-slate-800 text-white border-0"
                        >
                            {busy === 'add'
                                ? <Loader2 size={16} className="animate-spin" />
                                : <UserPlus size={16} />}
                        </Button>
                    </div>
                )}

                {/* List */}
                {loading ? (
                    <div className="space-y-2 animate-pulse">
                        {[0, 1, 2].map((i) => <div key={i} className="h-12 bg-gray-200 rounded-md" />)}
                    </div>
                ) : rows.length === 0 ? (
                    <div className="text-center text-sm text-muted-foreground py-6">
                        <p>No members yet.</p>
                        <p className="mt-1">
                            Nobody without a global role can see this yard until someone is added.
                        </p>
                    </div>
                ) : (
                    <div className="divide-y divide-border border border-border rounded-lg">
                        {rows.map((row) => {
                            const isLastManager = row.yardRole === 'yard_manager' && managerCount <= 1;
                            return (
                                <div key={row.key} className="flex items-center gap-3 p-3">
                                    <div className="min-w-0 flex-1">
                                        <p className="font-medium truncate flex items-center gap-1.5">
                                            {row.name}
                                            {row.yardRole === 'yard_manager' && (
                                                <ShieldCheck size={14} className="text-blue-600" />
                                            )}
                                        </p>
                                        <p className="text-xs text-muted-foreground truncate">
                                            {row.email}
                                        </p>
                                    </div>

                                    <Select
                                        value={row.yardRole}
                                        disabled={frozen || busy !== null}
                                        onValueChange={(v) => handleRoleChange(row, v as YardRole)}
                                    >
                                        <SelectTrigger className="w-[150px]">
                                            <SelectValue>{ROLE_LABEL[row.yardRole]}</SelectValue>
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="member">Member</SelectItem>
                                            <SelectItem value="yard_manager">Yard Manager</SelectItem>
                                        </SelectContent>
                                    </Select>

                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        aria-label={`Remove ${row.name}`}
                                        className={cn('text-destructive', (frozen || isLastManager) && 'opacity-40')}
                                        disabled={frozen || isLastManager || busy !== null}
                                        title={isLastManager ? 'This is the last Yard Manager' : undefined}
                                        onClick={() => handleRemove(row)}
                                    >
                                        <Trash2 size={16} />
                                    </Button>
                                </div>
                            );
                        })}
                    </div>
                )}

                {live && (
                    <p className="text-xs text-muted-foreground">
                        Removing someone revokes future access only. Their existing records stay
                        attributed to them.
                    </p>
                )}
            </CardContent>

            {/*
              * Removal is confirmed rather than immediate, unlike the staged rows above:
              * these are saved, so revoking access is not one click to undo.
              */}
            <AlertDialog
                open={pendingRemove !== null}
                onOpenChange={(open) => !open && setPendingRemove(null)}
            >
                <AlertDialogContent className="rounded-2xl">
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            Remove {pendingRemove?.name} from {yard?.yardName}?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            They lose access to this yard. Everything they created — orders,
                            photos, signatures — stays exactly as it is.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-xl" disabled={busy !== null}>
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            className="rounded-xl bg-red-600 hover:bg-red-700 text-white"
                            disabled={busy !== null}
                            onClick={(e) => {
                                // Kept open until the request settles so the spinner is visible.
                                e.preventDefault();
                                confirmRemove();
                            }}
                        >
                            {busy === `remove:${pendingRemove?.memberId}`
                                ? <Loader2 size={16} className="animate-spin" />
                                : 'Remove member'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Card>
    );
};

export default YardMembersSection;
